import { useEffect, useRef, useState } from 'react';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import {
  getElementAccessibleName,
  getElementSelectionCandidates,
  getElementViewportBounds,
  isElementSelectionChrome,
  resolveMeaningfulElement,
} from './elementSelection.js';

const SUPPRESSED_MOUSE_EVENTS = ['click', 'mousedown', 'mouseup', 'auxclick'];

export function ElementSelector() {
  const { state, cancelElementSelection, captureElementSelection } = useFeedbackMarkup();
  const [candidateBounds, setCandidateBounds] = useState(null);
  const [candidateAnnouncement, setCandidateAnnouncement] = useState('');
  const candidateRef = useRef(null);
  const candidateIndexRef = useRef(-1);
  const selectedRef = useRef(false);

  useEffect(() => {
    if (!state.elementSelecting) return undefined;
    selectedRef.current = false;

    const updateCandidate = (target) => {
      if (isElementSelectionChrome(target)) return;
      const candidate = resolveMeaningfulElement(target);
      candidateRef.current = candidate;
      candidateIndexRef.current = -1;
      setCandidateBounds(candidate ? getElementViewportBounds(candidate) : null);
      setCandidateAnnouncement(candidate ? getElementAccessibleName(candidate) || 'Unnamed item' : '');
    };
    const suppress = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };
    const onMouseMove = (event) => updateCandidate(event.target);
    const confirmCandidate = (candidate) => {
      const bounds = candidate ? getElementViewportBounds(candidate) : null;
      if (!candidate || !bounds) return;
      selectedRef.current = true;
      captureElementSelection({ element: candidate, bounds });
    };
    const onMouseActivation = (event) => {
      if (isElementSelectionChrome(event.target)) return;
      suppress(event);
      if (event.type !== 'click' || selectedRef.current) return;
      confirmCandidate(resolveMeaningfulElement(event.target) || candidateRef.current);
    };
    const cycleCandidate = (direction) => {
      const candidates = getElementSelectionCandidates();
      if (!candidates.length) return;
      const currentIndex = candidates.indexOf(candidateRef.current);
      const baseIndex = currentIndex >= 0 ? currentIndex : candidateIndexRef.current;
      const nextIndex =
        baseIndex < 0
          ? direction > 0
            ? 0
            : candidates.length - 1
          : (baseIndex + direction + candidates.length) % candidates.length;
      const candidate = candidates[nextIndex];
      candidateRef.current = candidate;
      candidateIndexRef.current = nextIndex;
      setCandidateBounds(getElementViewportBounds(candidate));
      setCandidateAnnouncement(getElementAccessibleName(candidate) || 'Unnamed item');
      candidate.focus?.({ preventScroll: true });
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        suppress(event);
        cancelElementSelection();
        return;
      }
      if (event.key === 'Tab') {
        suppress(event);
        cycleCandidate(event.shiftKey ? -1 : 1);
        return;
      }
      if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) {
        suppress(event);
        cycleCandidate(['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        suppress(event);
        if (!selectedRef.current) {
          confirmCandidate(candidateRef.current || resolveMeaningfulElement(event.target));
        }
      }
    };
    const onScroll = () => {
      const candidate = candidateRef.current;
      setCandidateBounds(candidate ? getElementViewportBounds(candidate) : null);
    };

    window.addEventListener('mousemove', onMouseMove, true);
    for (const eventName of SUPPRESSED_MOUSE_EVENTS) {
      window.addEventListener(eventName, onMouseActivation, true);
    }
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousemove', onMouseMove, true);
      for (const eventName of SUPPRESSED_MOUSE_EVENTS) {
        window.removeEventListener(eventName, onMouseActivation, true);
      }
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      candidateRef.current = null;
      candidateIndexRef.current = -1;
    };
  }, [cancelElementSelection, captureElementSelection, state.elementSelecting]);

  if (!state.elementSelecting) return null;

  return (
    <div
      className="feedback-element-selector"
      data-testid="feedback-element-selector"
    >
      <div className="feedback-element-instruction">
        <span role="status" aria-live="assertive">
          Select the page item you want to comment on. Use Tab or Arrow keys to move, Enter to
          select, and Esc to cancel.
          {candidateAnnouncement ? ` Current item: ${candidateAnnouncement}.` : ''}
        </span>
        <button
          type="button"
          aria-label="Cancel element selection"
          onClick={cancelElementSelection}
        >
          Cancel
        </button>
      </div>
      {candidateBounds ? (
        <div
          className="feedback-element-highlight"
          data-testid="feedback-element-highlight"
          aria-hidden="true"
          style={{
            left: candidateBounds.x,
            top: candidateBounds.y,
            width: candidateBounds.width,
            height: candidateBounds.height,
          }}
        />
      ) : null}
    </div>
  );
}
